import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faTimes, 
  faExternalLinkAlt,
  faSpinner,
  faCalendarAlt,
  faChevronDown,
  faCheck,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { doc, collection, getDoc, getDocs, addDoc, query, where, limit, orderBy, deleteDoc, updateDoc, increment } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import styles from './EndorsementsSection.module.css';
import defaultProfilePic from '../../assets/Images/defaultUserProfilePicture.png';

// Generate month options for dropdowns
const generateMonthOptions = () => {
  return [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
};

// Generate year options for dropdowns
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= currentYear - 50; i--) {
    years.push({ value: i.toString(), label: i.toString() });
  }
  return years;
};

const EndorsementsSection = ({ userData, showCustomAlert }) => {
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [brandSearchResults, setBrandSearchResults] = useState([]);
  const [displayedCurrentBrands, setDisplayedCurrentBrands] = useState([]);
  const [displayedPastBrands, setDisplayedPastBrands] = useState([]);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState('current');
  const [isPastBrand, setIsPastBrand] = useState(false);
  
  // State for brand selection
  const [selectedBrandForDates, setSelectedBrandForDates] = useState(null);
  
  // Ref for search input
  const searchInputRef = useRef(null);
  
  // For "Since" date
  const [sinceMonth, setSinceMonth] = useState('');
  const [sinceYear, setSinceYear] = useState('');
  
  // For past endorsements "Duration" date
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');

  const [pendingCurrentBrands, setPendingCurrentBrands] = useState([]);
const [pendingPastBrands, setPendingPastBrands] = useState([]);
const [isInitialized, setIsInitialized] = useState(false);


  // Load initial data
  useEffect(() => {
    if (userData?.userId) {
      // Set displayed brands from the arrays in userData - filter out any with isPending flag
      const currentBrands = userData.endorsedBrands || [];
      const pastBrands = userData.pastEndorsedBrands || [];
      
      // Only show confirmed brands
      setDisplayedCurrentBrands(currentBrands.filter(brand => !brand.isPending));
      setDisplayedPastBrands(pastBrands.filter(brand => !brand.isPending));
      
      // Set pending brands from the main arrays if they have isPending flag
      setPendingCurrentBrands(currentBrands.filter(brand => brand.isPending));
      setPendingPastBrands(pastBrands.filter(brand => brand.isPending));
      
      // Also fetch any pending requests from subcollection
      fetchPendingEndorsements();
      
      // Check if there are more based on counter fields
      setHasMoreCurrent((userData.endorsedBrandsCount || 0) > (userData.endorsedBrands?.length || 0));
      setHasMorePast((userData.pastEndorsedBrandsCount || 0) > (userData.pastEndorsedBrands?.length || 0));
      
      // Mark initialization as complete
      setIsInitialized(true);
    }
  }, [userData?.userId, userData?.endorsedBrands, userData?.pastEndorsedBrands, userData?.endorsedBrandsCount, userData?.pastEndorsedBrandsCount]);
  
   // Function to handle search input change
   const handleSearchInputChange = (e) => {
    setBrandSearchTerm(e.target.value);
  };

  const handleBrandSearch = async (e) => {
    // If called from form submit, prevent default
    if (e) {
      e.preventDefault();
    }
    
    // Get the value directly from the ref
    const term = searchInputRef.current ? searchInputRef.current.value : '';
    
    if (!term.trim()) return;
    
    // Update the state once on search (not on every keystroke)
    setBrandSearchTerm(term);
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      let allResults = [];
      
      // Case insensitive search
      const searchTerm = term.toLowerCase().trim();
      
      // Process both chunks with a generic approach
      const chunks = [
        { id: 'brands_chunk1', fieldName: 'data' },
        { id: 'brands_chunk2', fieldName: 'brandsDetails' }
      ];
      
      for (const chunk of chunks) {
        try {
          const docRef = doc(db, 'musicalBrands', chunk.id);
          const docSnapshot = await getDoc(docRef);
          
          if (docSnapshot.exists()) {
            const docData = docSnapshot.data();
            const brandsArray = docData[chunk.fieldName] || [];
            
            const results = brandsArray.filter(brand => 
              brand.brandName && brand.brandName.toLowerCase().includes(searchTerm)
            );
            
            allResults = [...allResults, ...results];
          }
        } catch (error) {
          console.error(`Error processing ${chunk.id}:`, error);
        }
      }
      
      // Sort and limit results
      allResults.sort((a, b) => a.brandName.localeCompare(b.brandName));
      
      // Check if brands are already added
      const currentBrandNames = displayedCurrentBrands.map(brand => brand.brandName.toLowerCase());
      const pastBrandNames = displayedPastBrands.map(brand => brand.brandName.toLowerCase());
      
      const filteredResults = allResults.filter(brand => {
        const brandNameLower = brand.brandName.toLowerCase();
        return isPastBrand
          ? !pastBrandNames.includes(brandNameLower)
          : !currentBrandNames.includes(brandNameLower);
      });
      
      setBrandSearchResults(filteredResults.slice(0, 10));
      setHasSearched(true);
      
      // No need to refocus here as the input won't lose focus during typing
    } catch (error) {
      console.error('Error searching brands:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to search brands. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add function to fetch pending endorsements
  const fetchPendingEndorsements = async () => {
    if (!userData?.userId) return;
    
    try {
      setIsLoading(true);
      const db = getFirestoreInstance();
      
      const pendingRef = collection(db, 'users', userData.userId, 'pendingEndorsements');
      const pendingSnapshot = await getDocs(pendingRef);
      
      if (!pendingSnapshot.empty) {
        // Create arrays for current and past pending endorsements
        const currentPending = [];
        const pastPending = [];
        
        pendingSnapshot.docs.forEach(doc => {
          const endorsementData = {
            ...doc.data(),
            docId: doc.id,
            docRef: doc.ref,
            isPending: true
          };
          
          // Sort into current or past based on isPast flag
          if (endorsementData.isPast) {
            pastPending.push(endorsementData);
          } else {
            currentPending.push(endorsementData);
          }
        });
        
        // Update state with pending endorsements - avoid duplicates by checking endorsementId
        setPendingCurrentBrands(prev => {
          const existingIds = new Set(prev.map(brand => brand.endorsementId));
          const newPendingBrands = currentPending.filter(req => !existingIds.has(req.endorsementId));
          return [...prev, ...newPendingBrands];
        });
        
        setPendingPastBrands(prev => {
          const existingIds = new Set(prev.map(brand => brand.endorsementId));
          const newPendingBrands = pastPending.filter(req => !existingIds.has(req.endorsementId));
          return [...prev, ...newPendingBrands];
        });
      }
    } catch (error) {
      console.error('Error fetching pending endorsements:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load pending endorsement requests.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  

  const loadMoreBrands = async (isPast) => {
    if (loadingMore || !userData?.userId) return;
    
    setLoadingMore(true);
    try {
      const db = getFirestoreInstance();
      const endorsementsRef = collection(db, 'users', userData.userId, 'endorsements');
      
      // Get currently displayed brands
      const displayedBrands = isPast ? displayedPastBrands : displayedCurrentBrands;
      
      // Skip items we already have
      const brandsQuery = query(
        endorsementsRef,
        where('isPast', '==', isPast),
        orderBy('createdAt', 'desc'),
        limit(displayedBrands.length + 3) // Get next 6 brands
      );
      
      const brandsSnapshot = await getDocs(brandsQuery);
      
      if (!brandsSnapshot.empty) {
        const allBrands = brandsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter out brands that are already displayed
        const displayedBrandNames = displayedBrands.map(brand => brand.brandName.toLowerCase());
        const newBrands = allBrands.filter(brand => 
          !displayedBrandNames.includes(brand.brandName.toLowerCase())
        );
        
        // Update displayed brands
        if (isPast) {
          setDisplayedPastBrands([...displayedBrands, ...newBrands]);
          // Check if we've loaded all past brands
          setHasMorePast(displayedPastBrands.length + newBrands.length < (userData.pastEndorsedBrandsCount || 0));
        } else {
          setDisplayedCurrentBrands([...displayedBrands, ...newBrands]);
          // Check if we've loaded all current brands
          setHasMoreCurrent(displayedCurrentBrands.length + newBrands.length < (userData.endorsedBrandsCount || 0));
        }
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} brands:`, error);
      showCustomAlert({
        title: 'Error',
        message: `Failed to load more ${isPast ? 'past' : 'current'} brand endorsements.`,
        type: 'error'
      });
    } finally {
      setLoadingMore(false);
    }
  };

  // Add function to handle canceling pending endorsements
const handleCancelPendingEndorsement = async (brand) => {
  if (!brand.docRef && !brand.docId) return;
  
  try {
    setIsLoading(true);
    
    if (brand.docRef) {
      await deleteDoc(brand.docRef);
    } else {
      const db = getFirestoreInstance();
      await deleteDoc(doc(db, 'users', userData.userId, 'pendingEndorsements', brand.docId));
    }
    
    // Remove from UI
    if (brand.isPast) {
      setPendingPastBrands(prev => 
        prev.filter(item => item.endorsementId !== brand.endorsementId)
      );
    } else {
      setPendingCurrentBrands(prev => 
        prev.filter(item => item.endorsementId !== brand.endorsementId)
      );
    }
    
    showCustomAlert({
      title: 'Success',
      message: 'Pending endorsement request canceled',
      type: 'success'
    });
  } catch (error) {
    console.error('Error canceling endorsement request:', error);
    showCustomAlert({
      title: 'Error',
      message: 'Failed to cancel the request',
      type: 'error'
    });
  } finally {
    setIsLoading(false);
  }
};

  // Handle initial brand selection
  const handleInitialBrandSelection = (brand) => {
    setSelectedBrandForDates(brand);
    // Clear date fields
    setSinceMonth('');
    setSinceYear('');
    setStartMonth('');
    setStartYear('');
    setEndMonth('');
    setEndYear('');
  };
  
  // Handle cancel selection - go back to search
  const handleCancelSelection = () => {
    setSelectedBrandForDates(null);
    // Focus back on search input when returning to search
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 0);
  };
  
  // Handle final brand selection with dates
  const handleConfirmSelection = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!selectedBrandForDates) return;
    
    // Validate date fields
    if (isPastBrand) {
      if (!startMonth || !startYear || !endMonth || !endYear) {
        showCustomAlert({
          title: 'Missing Information',
          message: 'Please select start and end dates for your past endorsement.',
          type: 'warning'
        });
        return;
      }
      
      // Validate date logic (start before end)
      const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1);
      const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1);
      
      if (startDate > endDate) {
        showCustomAlert({
          title: 'Invalid Date Range',
          message: 'Start date must be before end date.',
          type: 'warning'
        });
        return;
      }
      
      addPastBrandEndorsement(selectedBrandForDates);
    } else {
      if (!sinceMonth || !sinceYear) {
        showCustomAlert({
          title: 'Missing Information',
          message: 'Please select when your endorsement started.',
          type: 'warning'
        });
        return;
      }
      
      addCurrentBrandEndorsement(selectedBrandForDates);
    }
  };

  const addCurrentBrandEndorsement = async (brand) => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // Add since date to the brand data and ensure we have a unique ID
      const endorsementId = `${brand.brandName}-${Date.now()}`;
      const brandWithDate = {
        ...brand,
        since: {
          month: sinceMonth,
          year: sinceYear
        },
        isPast: false,
        createdAt: new Date().toISOString(),
        status: 'pending',
        endorsementId // Add a unique ID to help with identification
      };
      
      // Add to pendingEndorsements subcollection instead of direct addition
      const pendingEndorsementsRef = collection(db, 'users', userData.userId, 'pendingEndorsements');
      const newEndorsementDoc = await addDoc(pendingEndorsementsRef, brandWithDate);
      
      // Add to displayed brands with pending status
      const pendingBrand = {
        ...brandWithDate,
        docRef: newEndorsementDoc,
        isPending: true
      };
      
      // Update displayed current brands with the new pending request at the top
      setDisplayedCurrentBrands(prev => [pendingBrand, ...prev]);
      
      showCustomAlert({
        title: 'Request Sent',
        message: 'Brand endorsement request sent. Waiting for admin approval.',
        type: 'success'
      });
      
      // Reset form and close modal
      setSinceMonth('');
      setSinceYear('');
      setBrandSearchTerm('');
      setBrandSearchResults([]);
      setHasSearched(false);
      setSelectedBrandForDates(null);
      setShowBrandModal(false);
    } catch (error) {
      console.error('Error adding brand endorsement request:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to add brand endorsement request. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Similarly, update the addPastBrandEndorsement function
  const addPastBrandEndorsement = async (brand) => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // Add unique ID to help with identification
      const endorsementId = `${brand.brandName}-${Date.now()}`;
      
      // Add duration to the brand data
      const brandWithDuration = {
        ...brand,
        duration: {
          startMonth,
          startYear,
          endMonth,
          endYear
        },
        isPast: true,
        createdAt: new Date().toISOString(),
        status: 'pending',
        endorsementId
      };
      
      // Add to pendingEndorsements subcollection instead of direct addition
      const pendingEndorsementsRef = collection(db, 'users', userData.userId, 'pendingEndorsements');
      const newEndorsementDoc = await addDoc(pendingEndorsementsRef, brandWithDuration);
      
      // Add to displayed brands with pending status
      const pendingBrand = {
        ...brandWithDuration,
        docRef: newEndorsementDoc,
        isPending: true
      };
      
      // Update displayed past brands with the new pending request at the top
      setDisplayedPastBrands(prev => [pendingBrand, ...prev]);
      
      showCustomAlert({
        title: 'Request Sent',
        message: 'Past brand endorsement request sent. Waiting for admin approval.',
        type: 'success'
      });
      
      // Reset form and close modal
      setStartMonth('');
      setStartYear('');
      setEndMonth('');
      setEndYear('');
      setBrandSearchTerm('');
      setBrandSearchResults([]);
      setHasSearched(false);
      setSelectedBrandForDates(null);
      setShowBrandModal(false);
    } catch (error) {
      console.error('Error adding past brand endorsement request:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to add past brand endorsement request. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };



  const removeSelectedBrand = async (brandName, isPast) => {
    // Add validation for brandName
    if (!brandName || !userData?.userId) {
      showCustomAlert({
        title: 'Error',
        message: 'Invalid brand information',
        type: 'error'
      });
      return;
    }
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // Find the brand in subcollection - log for debugging
      console.log(`Trying to remove brand: ${brandName}, isPast: ${isPast}`);
      
      // Find the brand in subcollection
      const endorsementsRef = collection(db, 'users', userData.userId, 'endorsements');
      
      // First try a query with exact brandName
      let brandQuery = query(
        endorsementsRef,
        where('brandName', '==', brandName),
        where('isPast', '==', isPast)
      );
      
      let brandSnapshot = await getDocs(brandQuery);
      
      // If not found, try a case-insensitive search (Firebase doesn't support this directly)
      // So we'll need to get all endorsements and filter client-side
      if (brandSnapshot.empty) {
        console.log("Brand not found with exact match, trying case-insensitive search...");
        
        // Get all endorsements with matching isPast value
        const allEndorsementsQuery = query(
          endorsementsRef,
          where('isPast', '==', isPast)
        );
        
        const allEndorsementsSnapshot = await getDocs(allEndorsementsQuery);
        
        // Filter for case-insensitive brandName match
        const matchingDocs = allEndorsementsSnapshot.docs.filter(doc => {
          const data = doc.data();
          return data.brandName && 
                 data.brandName.toLowerCase() === brandName.toLowerCase();
        });
        
        // Create a new "snapshot" with the matching documents
        if (matchingDocs.length > 0) {
          console.log(`Found ${matchingDocs.length} matches with case-insensitive search`);
          brandSnapshot = { 
            empty: false,
            docs: matchingDocs
          };
        }
      }
      
      if (!brandSnapshot.empty) {
        // Delete from subcollection
        console.log(`Found ${brandSnapshot.docs.length} documents to delete`);
        
        for (const docSnapshot of brandSnapshot.docs) {
          await deleteDoc(docSnapshot.ref);
          console.log(`Deleted document with ID: ${docSnapshot.id}`);
        }
        
        // Get new top 3 for main document
        const newBrandsQuery = query(
          endorsementsRef,
          where('isPast', '==', isPast),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const newBrandsSnapshot = await getDocs(newBrandsQuery);
        const top3Brands = newBrandsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Update main document
        const userRef = doc(db, 'users', userData.userId);
        
        if (isPast) {
          await updateDoc(userRef, {
            pastEndorsedBrands: top3Brands,
            pastEndorsedBrandsCount: increment(-1) // Decrement the counter
          });
          
          // Update local state
          setDisplayedPastBrands(prev => prev.filter(b => 
            b.brandName.toLowerCase() !== brandName.toLowerCase()
          ));
          
          // Update hasMore flag
          setHasMorePast((userData.pastEndorsedBrandsCount || 0) - 1 > top3Brands.length);
        } else {
          await updateDoc(userRef, {
            endorsedBrands: top3Brands,
            endorsedBrandsCount: increment(-1) // Decrement the counter
          });
          
          // Update local state
          setDisplayedCurrentBrands(prev => prev.filter(b => 
            b.brandName.toLowerCase() !== brandName.toLowerCase()
          ));
          
          // Update hasMore flag
          setHasMoreCurrent((userData.endorsedBrandsCount || 0) - 1 > top3Brands.length);
        }
        
        showCustomAlert({
          title: 'Success',
          message: `${isPast ? 'Past b' : 'B'}rand endorsement removed successfully`,
          type: 'success'
        });
      } else {
        console.error(`Brand not found: ${brandName} (isPast: ${isPast})`);
        
        // Even if we couldn't find it in Firestore, remove it from the UI
        if (isPast) {
          setDisplayedPastBrands(prev => prev.filter(b => 
            b.brandName.toLowerCase() !== brandName.toLowerCase()
          ));
        } else {
          setDisplayedCurrentBrands(prev => prev.filter(b => 
            b.brandName.toLowerCase() !== brandName.toLowerCase()
          ));
        }
        
        showCustomAlert({
          title: 'Warning',
          message: 'Brand was removed from your profile. Some data might not have been found in the database.',
          type: 'warning'
        });
      }
    } catch (error) {
      console.error('Error removing brand:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to remove brand endorsement. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format "since" date for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    const monthName = generateMonthOptions().find(m => m.value === month)?.label.substring(0, 3) || '';
    
    return `Since ${monthName} ${year}`;
  };
  
  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    const startMonthName = generateMonthOptions().find(m => m.value === startMonth)?.label.substring(0, 3) || '';
    const endMonthName = generateMonthOptions().find(m => m.value === endMonth)?.label.substring(0, 3) || '';
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };

  // Brand Search Modal
  const BrandSearchModal = () => {
    if (!showBrandModal) return null;
    
    return (
      <div 
        className={styles.modalOverlay} 
        onClick={(e) => {
          // Only close if clicking directly on the overlay, not its children
          if (e.target === e.currentTarget) {
            setShowBrandModal(false);
            setSelectedBrandForDates(null);
          }
        }}
      >
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>
              {selectedBrandForDates 
                ? `Add ${selectedBrandForDates.brandName} as ${isPastBrand ? 'Past ' : ''}Endorsement` 
                : `Search ${isPastBrand ? 'Past ' : ''}Brands`}
            </h2>
            <button 
              className={styles.closeButton} 
              onClick={() => {
                setShowBrandModal(false);
                setSelectedBrandForDates(null);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {!selectedBrandForDates ? (
            // Search interface when no brand is selected yet
            <>
            <form onSubmit={handleBrandSearch} className={styles.searchForm}>
  <div className={styles.searchBar}>
    <input
      ref={searchInputRef}
      type="text"
      placeholder="Search brands..."
      className={styles.searchInput}
      // Remove the value and onChange props to make it uncontrolled
      defaultValue={brandSearchTerm}
    />
    <button 
      type="submit"
      className={styles.searchButton}
      disabled={isLoading}
    >
      {isLoading ? (
        <FontAwesomeIcon icon={faSpinner} spin />
      ) : (
        <FontAwesomeIcon icon={faSearch} />
      )}
      Search
    </button>
  </div>
</form>

              <div className={styles.searchResults}>
                {isLoading ? (
                  <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Searching...</p>
                  </div>
                ) : hasSearched ? (
                  brandSearchResults.length > 0 ? (
                    <div className={styles.resultsGrid}>
                      {brandSearchResults.map(brand => (
                        <div 
                          key={brand.brandName} 
                          className={styles.resultCard}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInitialBrandSelection(brand);
                          }}
                        >
                          <div className={styles.brandResultContent}>
                            <img 
                              src={brand.logo || defaultProfilePic} 
                              alt={brand.brandName}
                              className={styles.brandLogo}
                              onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePic; }}
                            />
                            <div className={styles.brandInfo}>
                              <h3>{brand.brandName}</h3>
                              {brand.website && (
                                <div className={styles.brandWebsite}>
                                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                                  <span>{brand.website}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noResults}>
                      No brands found for "{brandSearchTerm}"
                    </div>
                  )
                ) : (
                  <div className={styles.searchPrompt}>
                    Search for musical brands you endorse
                  </div>
                )}
              </div>
            </>
          ) : (
            // Date selection interface when a brand is selected
            <form onSubmit={handleConfirmSelection}>
              <div className={styles.selectedBrandContainer}>
                <div className={styles.selectedBrandHeader}>Selected Brand:</div>
                <div className={styles.selectedBrandDetails}>
                  <img 
                    src={selectedBrandForDates.logo || defaultProfilePic} 
                    alt={selectedBrandForDates.brandName}
                    className={styles.brandLogo}
                    onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePic; }}
                  />
                  <div className={styles.brandInfo}>
                    <h3>{selectedBrandForDates.brandName}</h3>
                    {selectedBrandForDates.website && (
                      <div className={styles.brandWebsite}>
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        <span>{selectedBrandForDates.website}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className={styles.dateSelection}>
                <h3>Endorsement {isPastBrand ? 'Duration' : 'Start Date'}</h3>
                
                {isPastBrand ? (
                  // Past Endorsement - Start and End Dates
                  <div className={styles.dateFields}>
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>Start Date:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={startMonth}
                          onChange={(e) => setStartMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={startYear}
                          onChange={(e) => setStartYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>End Date:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={endMonth}
                          onChange={(e) => setEndMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={endYear}
                          onChange={(e) => setEndYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Current Endorsement - Since Date
                  <div className={styles.dateFields}>
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>Since:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={sinceMonth}
                          onChange={(e) => setSinceMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={sinceYear}
                          onChange={(e) => setSinceYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className={styles.buttonContainer} style={{marginTop: '1rem', display: 'flex', gap: '1rem'}}>
                  <button 
                    type="button" 
                    className={styles.cancelButton}
                    onClick={handleCancelSelection}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      color: 'var(--text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    Back to Search
                  </button>
                  <button 
                    type="submit" 
                    className={styles.confirmButton}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#E2384D',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Add Endorsement
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  };

  // Return the endorsements section UI
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Endorsements</h2>
      
      {/* Tabs for Current/Past */}
      <div className={styles.endorsementTabs}>
        <button 
          className={`${styles.endorsementTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.endorsementTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      <button 
        className={styles.searchBrandButton}
        onClick={() => {
          setIsPastBrand(activeTab === 'past');
          setShowBrandModal(true);
          setBrandSearchTerm('');
          setBrandSearchResults([]);
          setHasSearched(false);
          setSelectedBrandForDates(null);
          // Focus the input when modal opens
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, 100);
        }}
      >
        <FontAwesomeIcon icon={faSearch} />
        Search {activeTab === 'past' ? 'Past ' : ''}Brands
      </button>
      
      {/* Current Endorsements */}
      {activeTab === 'current' && (
        <>
          {(displayedCurrentBrands.length > 0 || pendingCurrentBrands?.length > 0) ? (
            <div className={styles.selectedBrandsContainer}>
              <h3 className={styles.subsectionTitle}>Currently Endorsed By</h3>
              <div className={styles.selectedBrandsGrid}>
                {/* Render pending current endorsements first */}
                {pendingCurrentBrands && pendingCurrentBrands.map((brand) => (
                  <div key={brand.endorsementId || brand.brandName} className={`${styles.selectedBrandTag} ${styles.pendingTag}`}>
                    <img 
                      src={brand.logo || defaultProfilePic} 
                      alt={brand.brandName}
                      className={styles.selectedBrandLogo}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePic; }}
                    />
                    <div className={styles.brandDetails}>
                      <span className={styles.selectedBrandName}>{brand.brandName}</span>
                      {brand.since && (
                        <span className={styles.brandSince}>
                          <FontAwesomeIcon icon={faClock} className={styles.sinceIcon} />
                          {formatSince(brand.since)}
                        </span>
                      )}
                      <span className={styles.pendingBadge}>
                        <FontAwesomeIcon icon={faClock} /> Pending Approval
                      </span>
                    </div>
                    <button 
                      className={styles.removeBrandButton}
                      onClick={() => handleCancelPendingEndorsement(brand)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
  
                {/* Render confirmed current endorsements */}
                {displayedCurrentBrands.map((brand) => (
                  <div key={brand.brandName} className={styles.selectedBrandTag}>
                    <img 
                      src={brand.logo || defaultProfilePic} 
                      alt={brand.brandName}
                      className={styles.selectedBrandLogo}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePic; }}
                    />
                    <div className={styles.brandDetails}>
                      <span className={styles.selectedBrandName}>{brand.brandName}</span>
                      {brand.since && (
                        <span className={styles.brandSince}>
                          <FontAwesomeIcon icon={faClock} className={styles.sinceIcon} />
                          {formatSince(brand.since)}
                        </span>
                      )}
                    </div>
                    <button 
                      className={styles.removeBrandButton}
                      onClick={() => removeSelectedBrand(brand.brandName, false)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Load more button for current endorsements */}
              {hasMoreCurrent && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreBrands(false)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No current endorsements added yet. Click "Search Brands" to get started.</p>
            </div>
          )}
        </>
      )}
      
      {/* Past Endorsements */}
      {activeTab === 'past' && (
        <>
          {(displayedPastBrands.length > 0 || pendingPastBrands?.length > 0) ? (
            <div className={styles.selectedBrandsContainer}>
              <h3 className={styles.subsectionTitle}>Previously Endorsed By</h3>
              <div className={styles.selectedBrandsGrid}>
                {/* Render pending past endorsements first */}
                {pendingPastBrands && pendingPastBrands.map((brand) => (
                  <div key={brand.endorsementId || brand.brandName} className={`${styles.selectedBrandTag} ${styles.pendingTag}`}>
                    <img 
                      src={brand.logo || defaultProfilePic} 
                      alt={brand.brandName}
                      className={styles.selectedBrandLogo}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePic; }}
                    />
                    <div className={styles.brandDetails}>
                      <span className={styles.selectedBrandName}>{brand.brandName}</span>
                      {brand.duration && (
                        <span className={styles.brandDuration}>
                          <FontAwesomeIcon icon={faCalendarAlt} className={styles.durationIcon} />
                          {formatDuration(brand.duration)}
                        </span>
                      )}
                      <span className={styles.pendingBadge}>
                        <FontAwesomeIcon icon={faClock} /> Pending Approval
                      </span>
                    </div>
                    <button 
                      className={styles.removeBrandButton}
                      onClick={() => handleCancelPendingEndorsement(brand)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
  
                {/* Render confirmed past endorsements */}
                {displayedPastBrands.map((brand) => (
                  <div key={brand.brandName} className={styles.selectedBrandTag}>
                    <img 
                      src={brand.logo || defaultProfilePic} 
                      alt={brand.brandName}
                      className={styles.selectedBrandLogo}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePic; }}
                    />
                    <div className={styles.brandDetails}>
                      <span className={styles.selectedBrandName}>{brand.brandName}</span>
                      {brand.duration && (
                        <span className={styles.brandDuration}>
                          <FontAwesomeIcon icon={faCalendarAlt} className={styles.durationIcon} />
                          {formatDuration(brand.duration)}
                        </span>
                      )}
                    </div>
                    <button 
                      className={styles.removeBrandButton}
                      onClick={() => removeSelectedBrand(brand.brandName, true)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Load more button for past endorsements */}
              {hasMorePast && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreBrands(true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No past endorsements added yet. Click "Search Past Brands" to get started.</p>
            </div>
          )}
        </>
      )}
      
      {/* Brand Search Modal */}
      <BrandSearchModal />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
        </div>
      )}
    </div>
  );
};

export default EndorsementsSection;